// Plan omni-rick-round Phase 6 in the web: who an order's tickets follow, which tickets its
// plan opens, and how each one reads. The partner comes from the same pure rule the server's
// ticket plan uses (resolveOrderPartner), with the house dealer GET /api/dealers marks.
// Everything else comes from what the order's OrderTicket entries carry: kind, ticket id and
// number, queue id, checklist library id and state. The server sends no ticket title, no
// Autotask status and no checklist progress, so none of those are drawn.
import { resolveOrderPartner, type Dealer, type Order, type OrderPartner, type OrderTicket, type OrderTicketLive, type StepState, type TicketKind } from "@phoneware/lens-domain";

/** Whose sale it is, or a MAC (which opens no ticket of its own). */
export type OrderSide = OrderPartner | "mac";

/** The order's partner, decided the way the server's ticket plan decides it. null while the dealer is unknown. */
export function sideOf(o: Order, dealer: Dealer | null): OrderSide | null {
  if (o.kind === "mac") return "mac";
  if (!dealer || dealer.id !== o.dealerId) return null;
  return resolveOrderPartner(dealer, dealer.house ? dealer.id : null);
}

/** Orders signed before tickets followed the partner keep their one ticket (plan 3.3, reading old documents). */
export function followsPartner(o: Order): boolean {
  return o.kind !== "mac" && o.ticketPlan === "v1";
}

export function queueName(rowOrId: { queueName?: string | null } | number | null): string | null {
  if (rowOrId === null || rowOrId === 0) return null;
  if (typeof rowOrId === "number") return `Queue ${rowOrId}`;
  return rowOrId.queueName ?? null;
}

export function checklistName(row: { checklistName?: string | null; checklistLibraryId?: number | null } | number | null | undefined): string {
  if (row === null || row === undefined) return "";
  if (typeof row === "number") return `Checklist ${row}`;
  return row.checklistName ?? (row.checklistLibraryId ? `Checklist ${row.checklistLibraryId}` : "");
}

export function autotaskTicketUrl(row: { url?: string | null } | number): string {
  if (typeof row === "number") return "";
  return row.url ?? "";
}

/** One ticket the order has, or one its plan still has to open. */
export interface TicketRow {
  kind: TicketKind;
  /** null until Autotask has the ticket. */
  ticketId: number | null;
  ticketNumber: string | null;
  title: string | null;
  queueId: number | null;
  queueName: string | null;
  checklistLibraryId: number | null;
  checklistName: string | null;
  url: string | null;
  state: StepState;
  error: string | null;
  live: OrderTicketLive | null;
}

function fromTicket(t: OrderTicket): TicketRow {
  return {
    kind: t.kind,
    ticketId: t.ticketId || null,
    ticketNumber: t.ticketNumber || null,
    title: t.title || null,
    queueId: t.queueId || null,
    queueName: t.queueName || null,
    checklistLibraryId: t.checklistLibraryId ?? null,
    checklistName: t.checklistName || null,
    url: t.url || null,
    state: t.state,
    error: t.error,
    live: t.live ?? null,
  };
}

/**
 * The tickets as drawn. A MAC order has its source ticket. A new order has its order ticket, then a provisioning ticket
 * unless it is a reseller's sale. An order from before this round draws none: its one ticket is the step it always had.
 * The server records a ticket only once Autotask has it and keeps a failure on the order's autotask step, so a ticket
 * the plan still owes is drawn from that step: the first one missing carries its state and error, the rest wait.
 * With the partner unknown, only the tickets the order already has are drawn.
 */
export function ticketRows(o: Order, side: OrderSide | null): TicketRow[] {
  const have = o.tickets ?? [];
  if (o.kind === "mac") {
    const source = have.filter((t) => t.kind === "source").map(fromTicket);
    if (source.length) return source;
    const s = o.autotask;
    return [{
      kind: "source",
      ticketId: s.ticketId,
      ticketNumber: s.ticketNumber,
      title: "The support ticket the MAC came from",
      queueId: null,
      queueName: null,
      checklistLibraryId: null,
      checklistName: null,
      url: null,
      state: s.state,
      error: s.error,
      live: null,
    }];
  }
  if (!followsPartner(o)) return [];
  if (side === null) return have.map(fromTicket);
  const kinds: TicketKind[] = side === "reseller" ? ["order"] : ["order", "provisioning"];
  let owed = false;
  return kinds.map((kind) => {
    const found = have.find((t) => t.kind === kind);
    if (found) return fromTicket(found);
    const first = !owed;
    owed = true;
    const s = o.autotask;
    const state: StepState = first && (s.state === "failed" || s.state === "running") ? s.state : "pending";
    return {
      kind,
      ticketId: null,
      ticketNumber: null,
      title: null,
      queueId: null,
      queueName: null,
      checklistLibraryId: null,
      checklistName: null,
      url: null,
      state,
      error: state === "failed" ? s.error : null,
      live: null,
    };
  });
}

/** What a ticket of each kind is called when Autotask hasn't named its queue yet. */
const KIND_WORD: Record<TicketKind, string> = { order: "order", provisioning: "Provisioning", source: "source" };

/** "Projects", "Provisioning", "Billing": the queue a ticket sits in, or what kind of ticket it is before it exists. */
export function ticketLabel(row: TicketRow): string {
  return row.queueName ?? KIND_WORD[row.kind];
}

/** The stage label: one ticket or two, or the note a MAC puts on its source ticket. */
export function ticketStage(o: Order, side: OrderSide | null): string {
  if (o.kind === "mac") return "Note on the ticket";
  if (!followsPartner(o)) return "Ticket in Autotask";
  const known = side ?? (o.autotask.state === "done" && (o.tickets?.length ?? 0) === 1 ? "reseller" : null);
  return known === "reseller" ? "Ticket in Autotask" : "Tickets in Autotask";
}

/** "Phoneware's own sales", "Dealer", "Reseller", "MAC", as the ops list and order page tag an order. */
export const SIDE_WORD: Record<OrderSide, string> = { house: "Phoneware\u2019s own sales", dealer: "Dealer", reseller: "Reseller", mac: "MAC" };

/** "A Dealer's sale" and the like, for the tickets card. */
export const SIDE_NOTE: Record<OrderSide, string> = {
  house: "Phoneware\u2019s own sales",
  dealer: "A Dealer\u2019s sale",
  reseller: "A Reseller\u2019s sale",
  mac: "A MAC order",
};

/** A due date as people read it: "2026-11-03" is "Nov 3, 2026". Read as a calendar day, never shifted by a time zone. */
export function dueText(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** How many numbers a numbers note gives: its lines that say something. */
export function numberCount(note: string): number {
  return note.split("\n").filter((l) => l.trim()).length;
}
